import { Dispatch } from '@/utils/typeHelpers'
import { fetchWrapper } from '@/reducers/fetch'
import { userGetBookkeepingSubmission } from '../end-of-year-review/actions'
import {
  fetchStepContextsStart,
  fetchStepContextsSuccess,
  fetchStepContextsFailure,
} from './documentUpload.slice'
import {
  EndOfYearAdminReviewStep,
  IDocumentsStepContext,
} from '../end-of-year-review/types'

/**
 * Document-related step identifiers.
 *
 * These are the steps from the end-of-year review process that involve
 * document uploads. Used to filter the submission response.
 */
const DOCUMENT_STEPS: ReadonlyArray<EndOfYearAdminReviewStep> = [
  EndOfYearAdminReviewStep.docsPayroll,
  EndOfYearAdminReviewStep.docsContractorPayments,
  EndOfYearAdminReviewStep.docsProcessingFees,
  EndOfYearAdminReviewStep.docsPreviousTaxReturn,
  EndOfYearAdminReviewStep.docsAssetsOver2500,
  EndOfYearAdminReviewStep.docsOther,
]

/**
 * Fetch key for the fetchWrapper to prevent duplicate concurrent requests.
 */
export const FETCH_DOCUMENT_CONTEXTS_KEY = 'FETCH_DOCUMENT_CONTEXTS_KEY'

/**
 * Fetches all document step contexts in a single batch request.
 * Extracts only the document-related steps and dispatches to Redux.
 *
 * **Usage:**
 * ```tsx
 * const dispatch = useDispatch()
 *
 * useEffect(() => {
 *   dispatch(fetchDocumentContexts())
 * }, [dispatch])
 * ```
 *
 * @returns Promise resolving to map of step contexts keyed by step ID
 */
export const fetchDocumentContexts = () =>
  fetchWrapper({
    fetchKey: FETCH_DOCUMENT_CONTEXTS_KEY,
    defaultErrorMessage: 'Error fetching document upload contexts',
    fetchFunction: async (dispatch: Dispatch) => {
      // Mark all contexts as loading
      dispatch(fetchStepContextsStart())

      try {
        // Single batch API request for all step contexts
        const submission = await userGetBookkeepingSubmission()(dispatch)

        if (!submission?.steps) {
          throw new Error('No submission data found')
        }

        // Extract document-related step contexts
        const stepContexts: Record<string, IDocumentsStepContext> = {}

        submission.steps.forEach((step) => {
          if (DOCUMENT_STEPS.includes(step.step as EndOfYearAdminReviewStep)) {
            // Safe: API contract guarantees document steps have IDocumentsStepContext
            stepContexts[step.step] = step.context as IDocumentsStepContext
          }
        })

        // Dispatch success with all contexts at once
        dispatch(fetchStepContextsSuccess(stepContexts))

        return stepContexts
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Unknown error fetching document contexts'

        dispatch(fetchStepContextsFailure(errorMessage))

        throw error
      }
    },
  })

/**
 * Fetches a single step context.
 *
 * **Note:** This is less efficient than `fetchDocumentContexts()` for
 * fetching multiple contexts. Use `fetchDocumentContexts()` instead when
 * loading the full document review page.
 *
 * This function is useful when you need to refresh a single step context
 * after an update (e.g., after uploading a document).
 *
 * @param stepId - The step identifier to fetch
 * @returns Promise resolving to the step context
 */
export const fetchSingleStepContext = (stepId: EndOfYearAdminReviewStep) =>
  fetchWrapper({
    fetchKey: `FETCH_STEP_CONTEXT_${stepId}`,
    defaultErrorMessage: `Error fetching ${stepId} context`,
    fetchFunction: async (dispatch: Dispatch) => {
      try {
        // Use the batch endpoint but extract only the requested step
        const submission = await userGetBookkeepingSubmission()(dispatch)

        if (!submission?.steps) {
          throw new Error('No submission data found')
        }

        const step = submission.steps.find((s) => s.step === stepId)

        if (!step) {
          throw new Error(`Step ${stepId} not found in submission`)
        }

        // Safe: API contract guarantees document steps have IDocumentsStepContext
        const context = step.context as IDocumentsStepContext

        // Update only this step in Redux
        dispatch(
          fetchStepContextsSuccess({
            [stepId]: context,
          })
        )

        return context
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : `Unknown error fetching ${stepId} context`

        dispatch(fetchStepContextsFailure(errorMessage))

        throw error
      }
    },
  })
